import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useWizard } from "@/lib/wizard-context";
import { useTenant } from "@/lib/tenant-context";
import WizardProgress from "@/components/WizardProgress";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Shield, FileText, Camera, CheckCircle2 } from "lucide-react";
import { platformService } from "@/services";
import type { ConsentSection, PhotoSection } from "@/components/ConsentCopyEditor";
import { DEFAULT_PRIVACY, DEFAULT_TERMS, DEFAULT_PHOTO } from "@/components/ConsentCopyEditor";
import DOMPurify from "dompurify";

const sanitizeIntro = (raw: string) =>
  DOMPurify.sanitize(raw.replace(/(".*?")/g, "<strong>$1</strong>"), {
    ALLOWED_TAGS: ["strong", "em", "b", "i", "a", "br", "span"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });

const STEPS = ["Privacy", "Terms", "Photo Tips"];

const ConsentFlow = () => {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const { consent, setConsent, resetWizard } = useWizard();
  const { tenant } = useTenant();
  const [step, setStep] = useState(0);

  // Reset wizard state when starting a new flow — but NOT if returning from email verification
  useEffect(() => {
    const hash = window.location.hash;
    const isAuthRedirect = hash.includes("access_token") || hash.includes("type=signup") || hash.includes("type=magiclink");
    if (!isAuthRedirect) {
      resetWizard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [privacy, setPrivacy] = useState<ConsentSection>(DEFAULT_PRIVACY);
  const [terms, setTerms] = useState<ConsentSection>(DEFAULT_TERMS);
  const [photo, setPhoto] = useState<PhotoSection>(DEFAULT_PHOTO);

  useEffect(() => {
    const load = async () => {
      const [privRes, termsRes, photoRes] = await Promise.all([
        platformService.getPlatformConfig("consent_privacy"),
        platformService.getPlatformConfig("consent_terms"),
        platformService.getPlatformConfig("consent_photo"),
      ]);
      if (privRes.data) {
        const val = (privRes.data as any).value;
        if (val && typeof val === "object" && val.title) setPrivacy(val as ConsentSection);
      }
      if (termsRes.data) {
        const val = (termsRes.data as any).value;
        if (val && typeof val === "object" && val.title) setTerms(val as ConsentSection);
      }
      if (photoRes.data) {
        const val = (photoRes.data as any).value;
        if (val && typeof val === "object" && val.title) setPhoto(val as PhotoSection);
      }
    };
    load();
  }, []);

  const handleConsent = () => {
    if (step === 0) {
      setConsent((prev) => ({ ...prev, privacyConsentedAt: new Date().toISOString() }));
      setStep(1);
    } else if (step === 1) {
      setConsent((prev) => ({ ...prev, termsAcceptedAt: new Date().toISOString() }));
      setStep(2);
    } else {
      setConsent((prev) => ({ ...prev, photoTipsViewedAt: new Date().toISOString() }));
      navigate(`/t/${tenant_slug}/configure`);
    }
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-background overflow-hidden">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => step > 0 ? setStep(step - 1) : navigate(`/t/${tenant_slug}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-display font-semibold text-sm">{tenant?.brandName || "Cardmoji"}</span>
      </div>

      <WizardProgress steps={STEPS} currentStep={step} />

      <div className="flex-1 overflow-y-auto px-4 pb-32 animate-fade-in" style={{ minHeight: 0 }}>
        {step === 0 && (
          <Card className="max-w-lg mx-auto shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{privacy.title}</CardTitle>
              </div>
              <CardDescription>{privacy.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[50vh] rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
                <p className="mb-3" dangerouslySetInnerHTML={{ __html: sanitizeIntro(privacy.introText) }} />
                <ul className="space-y-2 list-disc pl-4">
                  {privacy.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card className="max-w-lg mx-auto shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{terms.title}</CardTitle>
              </div>
              <CardDescription>{terms.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[50vh] rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
                <p className="mb-3" dangerouslySetInnerHTML={{ __html: sanitizeIntro(terms.introText) }} />
                {terms.items.map((item, i) => (
                  <p key={i} className="mb-2">{i + 1}. {item}</p>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="max-w-lg mx-auto shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{photo.title}</CardTitle>
              </div>
              <CardDescription>{photo.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-sm">
                {photo.tips.map(({ emoji, tip }, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-lg bg-muted/50 p-3">
                    <span className="text-lg">{emoji}</span>
                    <span className="text-muted-foreground">{tip}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fixed bottom button */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[960px] bg-background/80 backdrop-blur border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="max-w-lg mx-auto">
          <Button
            className="w-full gradient-primary text-primary-foreground font-semibold gap-2"
            onClick={handleConsent}
          >
            {step === 0 && (
              <>
                <Shield className="h-4 w-4" />
                {privacy.ctaLabel}
              </>
            )}
            {step === 1 && (
              <>
                <FileText className="h-4 w-4" />
                {terms.ctaLabel}
              </>
            )}
            {step === 2 && (
              <>
                <CheckCircle2 className="h-4 w-4" />
                {photo.ctaLabel}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsentFlow;
